import React, { useState, useEffect } from 'react';
import { createAppointment } from '../services/appointmentService';
import { validateEmail, validatePhone, validateName, validateMessage, validateDate, validateTime } from './FormValidation';
import { trackAppointmentBooking } from '../utils/analytics';
import { getBookedSlots } from '../services/calendarService';
import AppointmentCalendar from './AppointmentCalendar';
import Icon from './icons/Icon';
import './AppointmentForm.css';

const AppointmentForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    appointmentDate: '',
    appointmentTime: '',
    service: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
  const [showCalendar, setShowCalendar] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);

  const services = [
    'General Checkup',
    'Teeth Cleaning',
    'Cosmetic Dentistry',
    'Orthodontics',
    'Dental Implants',
    'Teeth Whitening',
    'Emergency Care',
    'Other'
  ];

  const timeSlots = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
    '17:00', '17:30', '18:00', '18:30', '19:00', '19:30'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Real-time validation for certain fields
    if (touched[name]) {
      validateField(name, value);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let validation = { valid: true, message: '' };

    switch (name) {
      case 'name':
        validation = validateName(value);
        break;
      case 'email':
        validation = validateEmail(value);
        break;
      case 'phone':
        validation = validatePhone(value);
        break;
      case 'appointmentDate':
        validation = validateDate(value, 1);
        break;
      case 'appointmentTime':
        validation = validateTime(value, formData.appointmentDate);
        break;
      case 'message':
        if (value) {
          validation = validateMessage(value, 10);
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [name]: validation.valid ? '' : validation.message
    }));
  };

  const getMinDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1); // Minimum 1 day in advance
    return today.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3); // 3 months in advance
    return maxDate.toISOString().split('T')[0];
  };

  // Disable past dates and dates beyond 3 months
  const isDateDisabled = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    
    return date < today || date > maxDate;
  };

  // Load booked slots when date changes
  useEffect(() => {
    if (formData.appointmentDate) {
      loadBookedSlots();
    } else {
      setBookedSlots([]);
    }
  }, [formData.appointmentDate]);

  const loadBookedSlots = async () => {
    try {
      const booked = await getBookedSlots(formData.appointmentDate);
      setBookedSlots(booked.map(slot => slot.time));
    } catch (error) {
      console.error('Error loading booked slots:', error);
      setBookedSlots([]);
    }
  };

  // Filter available time slots based on selected date
  const getAvailableTimeSlots = () => {
    if (!formData.appointmentDate) return timeSlots;
    
    const selectedDate = new Date(formData.appointmentDate);
    const dayOfWeek = selectedDate.getDay();
    
    let availableSlots = timeSlots;
    
    // Saturday hours end earlier
    if (dayOfWeek === 6) {
      availableSlots = timeSlots.filter(time => time <= '18:00');
    }
    
    // Filter out booked slots
    return availableSlots.filter(time => !bookedSlots.includes(time));
  };

  const handleCalendarTimeSelect = (time) => {
    setFormData(prev => ({
      ...prev,
      appointmentTime: time
    }));
    setShowCalendar(false);
    // Scroll to form
    setTimeout(() => {
      const formElement = document.querySelector('.appointment-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleCalendarDateChange = (date) => {
    const dateString = date instanceof Date ? date.toISOString().split('T')[0] : date;
    setFormData(prev => ({
      ...prev,
      appointmentDate: dateString,
      appointmentTime: '' // Reset time when date changes
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allFields = ['name', 'email', 'phone', 'appointmentDate', 'appointmentTime', 'service'];
    const newTouched = {};
    allFields.forEach(field => {
      newTouched[field] = true;
      validateField(field, formData[field]);
    });
    setTouched(prev => ({ ...prev, ...newTouched }));

    // Check if there are any errors
    const nameValidation = validateName(formData.name);
    const emailValidation = validateEmail(formData.email);
    const phoneValidation = validatePhone(formData.phone);
    const dateValidation = validateDate(formData.appointmentDate, 1);
    const timeValidation = validateTime(formData.appointmentTime, formData.appointmentDate);

    if (!nameValidation.valid || !emailValidation.valid || !phoneValidation.valid || 
        !dateValidation.valid || !timeValidation.valid || !formData.service) {
      return; // Don't submit if validation fails
    }

    setLoading(true);
    setSubmitStatus(null);

    try {
      // Combine date and time
      const appointmentDateTime = new Date(`${formData.appointmentDate}T${formData.appointmentTime}`);
      
      await createAppointment({
        ...formData,
        appointmentDate: appointmentDateTime
      });

      // Track appointment booking in Google Analytics
      trackAppointmentBooking();

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        appointmentDate: '',
        appointmentTime: '',
        service: '',
        message: ''
      });
      setErrors({});
      setTouched({});

      // Clear success message after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      console.error('Error submitting appointment:', error);
      setSubmitStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="appointment-form-container">
      <div className="appointment-view-toggle">
        <button
          type="button"
          className={`toggle-btn ${!showCalendar ? 'active' : ''}`}
          onClick={() => setShowCalendar(false)}
        >
          <Icon name="fileText" size={18} /> Form View
        </button>
        <button
          type="button"
          className={`toggle-btn ${showCalendar ? 'active' : ''}`}
          onClick={() => setShowCalendar(true)}
        >
          <Icon name="calendar" size={18} /> Calendar View
        </button>
      </div>

      {showCalendar ? (
        <div className="appointment-calendar-wrapper">
          <AppointmentCalendar
            selectedDate={formData.appointmentDate || new Date()}
            onTimeSlotSelect={handleCalendarTimeSelect}
            onDateChange={handleCalendarDateChange}
          />
          <div className="calendar-form-note">
            <p><Icon name="lightbulb" size={16} /> Click on an available time slot to select it, then fill out the form below.</p>
          </div>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="appointment-form">
        {submitStatus === 'success' && (
          <div className="form-message form-success">
            <Icon name="check" size={16} /> Appointment request submitted successfully! We'll contact you soon to confirm.
          </div>
        )}
        
        {submitStatus === 'error' && (
          <div className="form-message form-error">
            ✗ Failed to submit appointment. Please try again.
          </div>
        )}

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="name">Full Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            placeholder="Your full name"
            className={touched.name && errors.name ? 'input-error' : ''}
          />
          {touched.name && errors.name && (
            <small className="error-message">{errors.name}</small>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            placeholder="your.email@example.com"
            className={touched.email && errors.email ? 'input-error' : ''}
          />
          {touched.email && errors.email && (
            <small className="error-message">{errors.email}</small>
          )}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="phone">Phone Number *</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            placeholder="+255 755 214 841 or 0755 214 841"
            className={touched.phone && errors.phone ? 'input-error' : ''}
          />
          {touched.phone && errors.phone && (
            <small className="error-message">{errors.phone}</small>
          )}
          <small className="form-help">Tanzania format: +255 or 0 followed by 9 digits</small>
        </div>

        <div className="form-group">
          <label htmlFor="service">Service Needed *</label>
          <select
            id="service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            className={touched.service && !formData.service ? 'input-error' : ''}
          >
            <option value="">Select a service</option>
            {services.map(service => (
              <option key={service} value={service}>{service}</option>
            ))}
          </select>
          {touched.service && !formData.service && (
            <small className="error-message">Please select a service</small>
          )}
        </div>
      </div>

      <div className="form-row">
            <div className="form-group">
              <label htmlFor="appointmentDate">Preferred Date *</label>
              <input
                type="date"
                id="appointmentDate"
                name="appointmentDate"
                value={formData.appointmentDate}
                onChange={(e) => {
                  handleChange(e);
                  // Reset time when date changes
                  setFormData(prev => ({ ...prev, appointmentTime: '' }));
                }}
                onBlur={handleBlur}
                required
                min={getMinDate()}
                max={getMaxDate()}
                className={touched.appointmentDate && errors.appointmentDate ? 'input-error' : ''}
              />
              {touched.appointmentDate && errors.appointmentDate && (
                <small className="error-message">{errors.appointmentDate}</small>
              )}
              {!errors.appointmentDate && (
                <small className="form-help">Select a date between tomorrow and 3 months from now</small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="appointmentTime">Preferred Time *</label>
              <select
                id="appointmentTime"
                name="appointmentTime"
                value={formData.appointmentTime}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                disabled={!formData.appointmentDate}
                className={touched.appointmentTime && errors.appointmentTime ? 'input-error' : ''}
              >
                <option value="">
                  {formData.appointmentDate ? 'Select a time' : 'Select date first'}
                </option>
                {getAvailableTimeSlots().map(time => {
                  const isBooked = bookedSlots.includes(time);
                  return (
                    <option 
                      key={time} 
                      value={time}
                      disabled={isBooked}
                    >
                      {time} {parseInt(time.split(':')[0]) >= 12 ? 'PM' : 'AM'}
                      {isBooked ? ' (Booked)' : ''}
                    </option>
                  );
                })}
              </select>
              {touched.appointmentTime && errors.appointmentTime && (
                <small className="error-message">{errors.appointmentTime}</small>
              )}
              {!errors.appointmentTime && (
                <small className="form-help">
                  Office hours: Mon-Fri 8:00 AM - 8:00 PM, Sat 8:00 AM - 6:00 PM
                </small>
              )}
            </div>
      </div>

      <div className="form-group">
        <label htmlFor="message">Additional Notes</label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          onBlur={handleBlur}
          rows="4"
          placeholder="Any additional information or special requests..."
          className={touched.message && errors.message ? 'input-error' : ''}
        ></textarea>
        {touched.message && errors.message && (
          <small className="error-message">{errors.message}</small>
        )}
        {formData.message && (
          <small className="form-help">{formData.message.length} characters</small>
        )}
      </div>

      <button 
        type="submit" 
        className="btn-submit"
        disabled={loading}
      >
        {loading ? 'Submitting...' : 'Request Appointment'}
      </button>
    </form>
    </div>
  );
};

export default AppointmentForm;

